import React, { ReactElement, useState } from "react";
import {
  Box,
  Button,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import {
  black,
  darkGray,
  gray,
  lightGray,
  textBlack,
  white,
} from "../../Constants/Colors";
import customApplicationConfiguration from "../../../CustomApplicationConfiguration";

export interface LevelComponentProps {
  title: string;
  levelInfo?: ReactElement;
  minOrder: string;
  deliveryTime: string;
  onLevelBtnClick: () => void;
  imageURL: string;
}

const LevelComponent = (props: LevelComponentProps) => {
  const {
    title,
    levelInfo,
    minOrder,
    deliveryTime,
    onLevelBtnClick,
    imageURL,
  } = props;
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const [hover, setHover] = useState(false);
  const useStyles = makeStyles((theme) => ({
    boxOverview: {
      margin: 11,
      width: "28%",
      minHeight: "40.57vh",
      borderRadius: "5px",
      boxSizing: "border-box",
      backgroundColor: hover ? black : white,
      border: hover ? `1px solid ${black}` : `1px solid ${darkGray}`,
    },
    title: {
      fontSize: "27px",
      fontWeight: 900,
      lineHeight: "103%",
      color: hover ? white : textBlack,
      marginLeft: 7,
    },
    mblTitle: {
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "103%",
      padding: "16px 0px 0px 18px",
      color: hover ? white : textBlack,
    },
    imageBox: {
      backgroundColor: white,
      borderBottom: hover ? `1px solid ${black}` : `1px solid ${darkGray}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "5px 5px 0 0",
    },
    image: {
      width: "100%",
      height: "31vh",
      objectFit: "contain",
      borderRadius: "5px 5px 0 0",
    },
    levelContent: {
      padding: 30,
      [theme.breakpoints.down("sm")]: {
        padding: "0px",
      },
    },
    levelInfo: {
      height: "14.62vh",
      color: hover ? white : black,
      marginLeft: 7,
      [theme.breakpoints.down("sm")]: {
        fontSize: "11px",
        minHeight: "0px",
        marginLeft: 18,
      },
      "& .MuiTypography-body1": {
        color: hover ? `${white} !important` : "black",
      },
    },
    levelBtn: {
      width: "100%",
      marginTop: "30px",
      display: "flex",
      flexDirection: "row",
      padding: "12px 48px",
      gap: "10px",
      color: hover ? black : white,
      backgroundColor: hover ? white : theme.palette.primary.main,
      "&:hover": {
        backgroundColor: lightGray,
      },
    },
    orderInfo: {
      fontSize: "13px",
      lineHeight: "18px",
      letterSpacing: "0.327273px",
      color: hover ? white : textBlack,
      [theme.breakpoints.down("sm")]: {
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: "120%",
        letterSpacing: "0.3272px",
        color: lightGray,
      },
    },
    mblBoxOverView: {
      borderRadius: "5px 5px 5px 5px ",
      display: "flex",
      justifyContent: "space-between",
      width: "90%",
      height: "100px",
      margin: "35px 23px 5px 24px",
      backgroundColor: hover ? black : white,
      border: hover ? `1px solid ${black}` : `1px solid ${darkGray}`,
    },
    mblImageBox: {
      backgroundColor: white,
      borderRadius: "5px 0px 0px 5px",
      height: "100%",
      minWidth: "15vw",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRight: `1px solid ${darkGray}`,
    },
    mblImage: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
    mblLevelInfo: {
      width: "100%",
      fontSize: "11px",
    },
    mblButton: {
      background: black,
      color: white,
      borderRadius: "0px 5px 5px 0px !important",
      minWidth: "0px !important",
      borderLeft: hover ? "1px solid #C4C4C4" : `1px solid ${black}`,
    },
    mblOrderInfo: {
      display: "flex",
      paddingLeft: "6%",
      color: lightGray,
      placeSelf: "flex-start",
    },
    mblWrapprer: { cursor: "pointer", width: "100%" },
  }));
  const classes = useStyles();

  return (
    <>
      {!isMobile && (
        <Box
          onMouseEnter={() => {
            setHover(true);
          }}
          onMouseLeave={() => {
            setHover(false);
          }}
          className={classes.boxOverview}
        >
          <Box className={classes.imageBox}>
            <img
              className={classes.image}
              src={
                customApplicationConfiguration.network.resourcesURL + imageURL
              }
              alt="level image"
            ></img>
          </Box>
          <Box className={classes.levelContent}>
            <Typography className={classes.title}>{title}</Typography>
            <Box className={classes.levelInfo}>{levelInfo}</Box>
            <Box style={{ marginLeft: 7 }}>
              <Typography className={classes.orderInfo}>
                {t("levelOverview.levelComponent.minOrder")}&nbsp;{minOrder}
              </Typography>
              <Typography className={classes.orderInfo}>
                {t("levelOverview.levelComponent.deliveryTime")}&nbsp;
                {deliveryTime}
              </Typography>
            </Box>
            <Button className={classes.levelBtn} onClick={onLevelBtnClick}>
              {t("levelOverview.levelComponent.configuration")}
            </Button>
          </Box>
        </Box>
      )}
      {isMobile && (
        <Box onClick={onLevelBtnClick} className={classes.mblWrapprer}>
          <Box
            onMouseEnter={() => {
              setHover(true);
            }}
            onMouseLeave={() => {
              setHover(false);
            }}
            className={classes.mblBoxOverView}
          >
            <Box className={classes.mblImageBox}>
              <img
                className={classes.mblImage}
                src={
                  customApplicationConfiguration.network.resourcesURL + imageURL
                }
              ></img>
            </Box>
            <Box className={classes.mblLevelInfo}>
              <Typography className={classes.mblTitle}>{title}</Typography>
              <Box className={classes.levelInfo}>{levelInfo}</Box>
            </Box>
            <Button className={classes.mblButton} onClick={null}>
              <KeyboardArrowRightIcon />
            </Button>
          </Box>
          <Box className={classes.mblOrderInfo}>
            <Typography className={classes.orderInfo}>
              {t("levelOverview.levelComponent.minOrder")}&nbsp;{minOrder}
            </Typography>
            <Typography className={classes.orderInfo}>
              {t("levelOverview.levelComponent.deliveryTime")}&nbsp;
              {deliveryTime}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default LevelComponent;
